import React, { useState, useEffect, useRef } from 'react';
import { Form } from 'react-bootstrap';
import RolesService from '../../../services/RolesService';

const RoleSearchSelect = ({
    value,
    onChange,
    onBlur,
    name = 'roleId',
    placeholder = 'Search and select role...',
    error,
    isInvalid = false,
    required = false,
    disabled = false,
    multiple = false,
    label = 'Role'
}) => {
    const [options, setOptions] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectedOption, setSelectedOption] = useState(null);
    const [selectedOptions, setSelectedOptions] = useState([]);
    
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);

    // Load roles on component mount
    useEffect(() => {
        loadRoles();
    }, []);

    // Handle value prop changes
    useEffect(() => {
        if (multiple) {
            if (Array.isArray(value) && value.length > 0) {
                const selected = options.filter(option => value.includes(option.value));
                setSelectedOptions(selected);
                setSearchTerm('');
            } else {
                setSelectedOptions([]);
                setSearchTerm('');
            }
        } else {
            if (value && options.length > 0) {
                const selected = options.find(option => option.value === value);
                if (selected) {
                    setSelectedOption(selected);
                    setSearchTerm(`${selected.label} (${selected.roleCode || 'N/A'})`);
                } else {
                    setSelectedOption(null);
                    setSearchTerm('');
                }
            } else {
                setSelectedOption(null);
                setSearchTerm('');
            }
        }
    }, [value, options, multiple]);

    // Handle clicks outside component
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const loadRoles = async () => {
        setLoading(true);
        try {
            const response = await RolesService.getRoleDropdown();
            if (response.success && response.data) {
                setOptions(response.data);
            }
        } catch (error) {
            console.error('Error loading roles:', error);
            setOptions([]);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        setIsOpen(true);
        
        // Clear selection if search is cleared
        if (!term && !multiple) {
            setSelectedOption(null);
            if (onChange) {
                const syntheticEvent = {
                    target: { name, value: null }
                };
                onChange(syntheticEvent);
            }
        }
    };

    const handleOptionSelect = (option) => {
        if (multiple) {
            const isSelected = selectedOptions.some(opt => opt.value === option.value);
            let newSelection;
            
            if (isSelected) {
                newSelection = selectedOptions.filter(opt => opt.value !== option.value);
            } else {
                newSelection = [...selectedOptions, option];
            }
            
            setSelectedOptions(newSelection);
            const values = newSelection.map(opt => opt.value);
            if (onChange) {
                const syntheticEvent = {
                    target: { name, value: values }
                };
                onChange(syntheticEvent);
            }
            setSearchTerm('');
        } else {
            setSelectedOption(option);
            setSearchTerm(`${option.label} (${option.roleCode || 'N/A'})`);
            setIsOpen(false);
            if (onChange) {
                const syntheticEvent = {
                    target: { name, value: option.value }
                };
                onChange(syntheticEvent);
            }
        }
    };

    const handleRemoveOption = (optionToRemove) => {
        if (multiple) {
            const newSelection = selectedOptions.filter(opt => opt.value !== optionToRemove.value);
            setSelectedOptions(newSelection);
            const values = newSelection.map(opt => opt.value);
            if (onChange) {
                const syntheticEvent = {
                    target: { name, value: values }
                };
                onChange(syntheticEvent);
            }
        }
    };

    const handleClear = () => {
        if (multiple) {
            setSelectedOptions([]);
            if (onChange) {
                const syntheticEvent = {
                    target: { name, value: [] }
                };
                onChange(syntheticEvent);
            }
        } else {
            setSelectedOption(null);
            setSearchTerm('');
            if (onChange) {
                const syntheticEvent = {
                    target: { name, value: null }
                };
                onChange(syntheticEvent);
            }
        }
        setIsOpen(false);
    };

    const handleInputFocus = () => {
        setIsOpen(true);
    };

    const handleInputBlur = (e) => {
        // Delay blur to allow option selection
        setTimeout(() => {
            if (onBlur) {
                onBlur(e);
            }
        }, 150);
    };

    // Filter options based on search term
    const filteredOptions = options.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (option.roleCode && option.roleCode.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (option.description && option.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const getDisplayValue = () => {
        if (multiple) {
            return selectedOptions.length > 0 
                ? `${selectedOptions.length} role(s) selected`
                : '';
        }
        return searchTerm;
    };

    return (
        <Form.Group>
            {label && (
                <Form.Label>
                    {label} {required && <span className="text-danger">*</span>}
                </Form.Label>
            )}
            
            <div className="position-relative" ref={dropdownRef}>
                <div className="input-group">
                    <Form.Control
                        ref={inputRef}
                        type="text"
                        name={name}
                        value={getDisplayValue()}
                        onChange={handleInputChange}
                        onFocus={handleInputFocus}
                        onBlur={handleInputBlur}
                        placeholder={placeholder}
                        disabled={disabled}
                        isInvalid={isInvalid}
                        className={`${isInvalid ? 'is-invalid' : ''}`}
                        autoComplete="off"
                    />
                    
                    {/* Clear button */}
                    {((selectedOption && !multiple) || (selectedOptions.length > 0 && multiple)) && !disabled && (
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={handleClear}
                            style={{ 
                                borderLeft: 'none',
                                zIndex: 1000
                            }}
                        >
                            <i className="fa fa-times"></i>
                        </button>
                    )}
                    
                    {/* Dropdown arrow */}
                    <button
                        type="button"
                        className="btn btn-outline-secondary dropdown-toggle"
                        onClick={() => !disabled && setIsOpen(!isOpen)}
                        disabled={disabled}
                        style={{
                            borderLeft: ((selectedOption && !multiple) || (selectedOptions.length > 0 && multiple)) ? 'none' : undefined
                        }}
                    >
                        {loading && <i className="fa fa-spinner fa-spin me-1"></i>}
                    </button>
                </div>

                {/* Selected options display (for multiple selection) */}
                {multiple && selectedOptions.length > 0 && (
                    <div className="mt-2">
                        <div className="d-flex flex-wrap gap-1">
                            {selectedOptions.map((option) => (
                                <span key={option.value} className="badge bg-primary d-flex align-items-center">
                                    {option.label} ({option.roleCode})
                                    {!disabled && (
                                        <button
                                            type="button"
                                            className="btn-close btn-close-white ms-1"
                                            style={{ fontSize: '0.7em' }}
                                            onClick={() => handleRemoveOption(option)}
                                        ></button>
                                    )}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {/* Dropdown menu */}
                {isOpen && !disabled && (
                    <div 
                        className="dropdown-menu show w-100"
                        style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            zIndex: 1050,
                            maxHeight: '300px',
                            overflowY: 'auto'
                        }}
                    >
                        {loading ? (
                            <div className="dropdown-item text-center">
                                <i className="fa fa-spinner fa-spin me-2"></i>
                                Loading roles...
                            </div>
                        ) : filteredOptions.length > 0 ? (
                            filteredOptions.map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    className={`dropdown-item d-flex justify-content-between align-items-start ${
                                        multiple 
                                            ? selectedOptions.some(opt => opt.value === option.value) ? 'active' : ''
                                            : selectedOption?.value === option.value ? 'active' : ''
                                    }`}
                                    onClick={() => handleOptionSelect(option)}
                                >
                                    <div className="flex-grow-1">
                                        <div className="fw-bold">{option.label}</div>
                                        <small className="text-muted d-block">
                                            Code: {option.roleCode || 'N/A'}
                                        </small>
                                        {option.description && (
                                            <small className="text-muted d-block">
                                                {option.description}
                                            </small>
                                        )}
                                    </div>
                                    {multiple && selectedOptions.some(opt => opt.value === option.value) && (
                                        <i className="fa fa-check text-success"></i>
                                    )}
                                </button>
                            ))
                        ) : (
                            <div className="dropdown-item text-muted">
                                {searchTerm ? 'No roles found matching your search' : 'No roles available'}
                            </div>
                        )}
                    </div>
                )}

                {/* Error display */}
                {error && (
                    <Form.Control.Feedback type="invalid" className="d-block">
                        {error}
                    </Form.Control.Feedback>
                )}
            </div>
        </Form.Group>
    );
};

export default RoleSearchSelect;